import React, {useEffect, useState} from 'react';
import {useIntl} from "react-intl";
import {HttpStatus} from "../../../../modules/common/enums/HttpStatus";
import {WeChatApplicationDto} from "../../../../modules/wechat/dto/WeChatApplicationDto";
import {WeChatApplicationApi} from "../../../../modules/wechat/api/WeChatApplicationApi";
import {MessageUtils} from "../../../../utils/MessageUtils";
import {Dialog, DialogActions, DialogContent, DialogTitle,} from "@mui/material";
import FormFieldText from "../../../../components/FormFieldText";
import CancelButton from "../../../../components/buttons/CancelButton";
import SaveButton from "../../../../components/buttons/SaveButton";
import FormFieldSelect from "../../../../components/FormFieldSelect";
import {WeChatApplicationType} from "../../../../modules/wechat/enums/WeChatApplicationType";

export type WeChatApplicationDetailDialogProps = {
    open: boolean;
    wechatApplication: WeChatApplicationDto;
    onClose: () => void;
    onOk: () => void;
};

const WeChatApplicationDetailDialog: React.FC<WeChatApplicationDetailDialogProps> = (props) => {
    const intl = useIntl();
    const title = props.wechatApplication.id ? 'WECHAT_APPLICATION_UPDATE' : 'WECHAT_APPLICATION_CREATE';

    const [innerWeChatApplication, setInnerWeChatApplication] = useState<WeChatApplicationDto>(props.wechatApplication);

    const [messageMap, setMessageMap] = useState(new Map<string, string>());

    useEffect(() => {
        if (props.open) {
            setInnerWeChatApplication({...props.wechatApplication});
        }
    }, [props.open]);

    const handleChange = (name: string, value: any) => {
        setInnerWeChatApplication(previousInnerWeChatApplication => ({
            ...previousInnerWeChatApplication,
            [name]: value
        }));
    };

    const handleOk = async () => {
        if (validate()) {
            if (props.wechatApplication.id) {
                const responseResult = await WeChatApplicationApi.updateWeChatApplication(innerWeChatApplication);
                if (responseResult.status === HttpStatus.SUCCESS) {
                    MessageUtils.success(intl.formatMessage({id: 'WECHAT_APPLICATION_UPDATE_SUCCESS'}));
                    props.onClose();
                    props.onOk();
                }
            } else {
                const responseResult = await WeChatApplicationApi.createWeChatApplication(innerWeChatApplication);
                if (responseResult.status === HttpStatus.SUCCESS) {
                    MessageUtils.success(intl.formatMessage({id: 'WECHAT_APPLICATION_CREATE_SUCCESS'}));
                    props.onClose();
                    props.onOk();
                }
            }
        }
    };

    const handleCancel = () => {
        props.onClose();
    };

    const validate = () => {
        const newMessageMap = new Map<string, string>();
        if (innerWeChatApplication.name === '') {
            newMessageMap.set('name', intl.formatMessage({id: 'WECHAT_APPLICATION_NAME_REQUIRED'}));
        }
        if (innerWeChatApplication.appId === '') {
            newMessageMap.set('appId', intl.formatMessage({id: 'WECHAT_APPLICATION_APP_ID_REQUIRED'}));
        }
        if (innerWeChatApplication.appSecret === '') {
            newMessageMap.set('appSecret', intl.formatMessage({id: 'WECHAT_APPLICATION_APP_SECRET_REQUIRED'}));
        }
        if (innerWeChatApplication.type === '') {
            newMessageMap.set('type', intl.formatMessage({id: 'WECHAT_APPLICATION_TYPE_REQUIRED'}));
        }

        setMessageMap(newMessageMap);
        return newMessageMap.size <= 0;
    };

    return (
        <Dialog open={props.open} onClose={handleCancel} fullWidth={true}>
            <DialogTitle>
                {intl.formatMessage({id: title})}
            </DialogTitle>
            <DialogContent>
                <FormFieldText label={intl.formatMessage({id: "WECHAT_APPLICATION_NAME"})}
                               required={true} errorText={messageMap.get("name")}
                               value={innerWeChatApplication.name}
                               onChange={(value) => {
                                   handleChange("name", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "WECHAT_APPLICATION_APP_ID"})}
                               required={true} errorText={messageMap.get("appId")}
                               value={innerWeChatApplication.appId}
                               onChange={(value) => {
                                   handleChange("appId", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "WECHAT_APPLICATION_APP_SECRET"})}
                               required={true} errorText={messageMap.get("appSecret")}
                               value={innerWeChatApplication.appSecret}
                               onChange={(value) => {
                                   handleChange("appSecret", value)
                               }}/>
                <FormFieldSelect label={intl.formatMessage({id: "WECHAT_APPLICATION_TYPE"})}
                                 required={true} errorText={messageMap.get("type")}
                                 value={innerWeChatApplication.type}
                                 onChange={(value) => {
                                     handleChange("type", value)
                                 }}
                                 items={WeChatApplicationType.List.map((item) => {
                                         return {label: intl.formatMessage({id: item.label}), value: item.value}
                                     }
                                 )}/>
            </DialogContent>
            <DialogActions>
                <CancelButton onClick={handleCancel}/>
                <SaveButton onClick={handleOk}/>
            </DialogActions>
        </Dialog>
    );
};

export default WeChatApplicationDetailDialog;
